import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
function FloatingParticle({ index, total }: {index: number;total: number;}) {
  const { isDark } = useTheme();
  const config = useMemo(() => {
    const golden = 137.508;
    const seed = index * golden;
    const x = seed * 1.1 % 100;
    const y = (seed * 2.3 + index * 17) % 100;
    const isStar = index < 5;
    const size = isStar ? 6 + index % 3 * 2 : 3 + index % 3 * 1.5;
    const duration = isStar ? 8 + index % 4 * 2 : 14 + index % 8 * 2.5;
    const delay = index % 7 * 0.6;
    const driftX = (index % 2 === 0 ? 1 : -1) * (8 + index % 18);
    const driftY = (index % 3 === 0 ? -1 : 1) * (6 + index % 14);
    return {
      x,
      y,
      size,
      duration,
      delay,
      driftX,
      driftY,
      isStar
    };
  }, [index]);
  const baseOpacity = isDark ?
  config.isStar ?
  0.15 :
  0.2 + index % 5 * 0.08 :
  config.isStar ?
  0.12 :
  0.15 + index % 5 * 0.06;
  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        width: config.size,
        height: config.size,
        left: `${config.x}%`,
        top: `${config.y}%`,
        background: isDark ?
        `rgba(6, 182, 212, ${baseOpacity})` :
        `rgba(8, 145, 178, ${baseOpacity})`,
        ...(config.isStar && {
          animation: `twinkle ${3 + index % 3}s ease-in-out infinite ${index * 0.5}s`
        })
      }}
      animate={
      config.isStar ?
      {
        x: [0, config.driftX * 0.5, 0],
        y: [0, config.driftY * 0.5, 0]
      } :
      {
        x: [0, config.driftX, -config.driftX * 0.5, 0],
        y: [0, config.driftY, -config.driftY * 0.7, 0],
        opacity: [
        baseOpacity,
        baseOpacity * 2.5,
        baseOpacity * 1.5,
        baseOpacity]

      }
      }
      transition={{
        duration: config.duration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: config.delay
      }} />);


}
export function AnimatedBackground() {
  const { isDark } = useTheme();
  const darkOrbs = [
  {
    color: 'bg-indigo-900/40',
    size: 'w-[500px] h-[500px]',
    animateX: ['-10%', '5%', '-8%', '-10%'],
    animateY: ['-10%', '8%', '-5%', '-10%'],
    initialX: '-10%',
    initialY: '-10%',
    duration: 22,
    blur: 'blur-[70px]'
  },
  {
    color: 'bg-violet-800/30',
    size: 'w-[450px] h-[450px]',
    animateX: ['60%', '65%', '55%', '60%'],
    animateY: ['20%', '30%', '15%', '20%'],
    initialX: '60%',
    initialY: '20%',
    duration: 18,
    blur: 'blur-[70px]'
  },
  {
    color: 'bg-slate-800/50',
    size: 'w-[600px] h-[600px]',
    animateX: ['30%', '25%', '35%', '30%'],
    animateY: ['60%', '55%', '65%', '60%'],
    initialX: '30%',
    initialY: '60%',
    duration: 25,
    blur: 'blur-[70px]'
  },
  {
    color: 'bg-cyan-800/20',
    size: 'w-[400px] h-[400px]',
    animateX: ['70%', '75%', '65%', '70%'],
    animateY: ['40%', '35%', '45%', '40%'],
    initialX: '70%',
    initialY: '40%',
    duration: 20,
    blur: 'blur-[70px]'
  },
  {
    color: 'bg-indigo-800/25',
    size: 'w-[350px] h-[350px]',
    animateX: ['80%', '75%', '85%', '80%'],
    animateY: ['70%', '65%', '75%', '70%'],
    initialX: '80%',
    initialY: '70%',
    duration: 20,
    blur: 'blur-[70px]'
  }];

  const lightOrbs = [
  {
    color: 'bg-cyan-100/50',
    size: 'w-[500px] h-[500px]',
    animateX: ['-5%', '8%', '-3%', '-5%'],
    animateY: ['-5%', '10%', '-8%', '-5%'],
    initialX: '-5%',
    initialY: '-5%',
    duration: 24,
    blur: 'blur-[120px]'
  },
  {
    color: 'bg-indigo-100/35',
    size: 'w-[450px] h-[450px]',
    animateX: ['55%', '60%', '50%', '55%'],
    animateY: ['15%', '25%', '10%', '15%'],
    initialX: '55%',
    initialY: '15%',
    duration: 20,
    blur: 'blur-[70px]'
  },
  {
    color: 'bg-violet-100/25',
    size: 'w-[550px] h-[550px]',
    animateX: ['25%', '20%', '30%', '25%'],
    animateY: ['55%', '50%', '60%', '55%'],
    initialX: '25%',
    initialY: '55%',
    duration: 26,
    blur: 'blur-[130px]'
  },
  {
    color: 'bg-cyan-50/60',
    size: 'w-[400px] h-[400px]',
    animateX: ['75%', '70%', '80%', '75%'],
    animateY: ['45%', '40%', '50%', '45%'],
    initialX: '75%',
    initialY: '45%',
    duration: 22,
    blur: 'blur-[70px]'
  }];

  const orbs = isDark ? darkOrbs : lightOrbs;
  const particleCount = 10;
  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{
        zIndex: 0
      }}>

      {orbs.map((orb, i) =>
      <motion.div
        key={`orb-${i}`}
        className={`absolute rounded-full ${orb.color} ${orb.size} ${orb.blur}`}
        initial={{
          x: orb.initialX,
          y: orb.initialY
        }}
        animate={{
          x: orb.animateX,
          y: orb.animateY
        }}
        transition={{
          duration: orb.duration,
          repeat: Infinity,
          repeatType: 'loop',
          ease: 'easeInOut'
        }}
        style={{
          willChange: 'transform'
        }} />

      )}
      {Array.from({
        length: particleCount
      }).map((_, i) =>
      <FloatingParticle key={`p-${i}`} index={i} total={particleCount} />
      )}
    </div>);

}